import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import membershipService from '../api/membershipService';
import { getAndValidateToken, handleInvalidToken } from '../utils/tokenValidation';
import { useDataUser } from './dataUserContext';

const PUBLIC_ROUTES = [
  '/login',
  '/',
  '/registro',
  '/registro-gratuito',
  '/registro-pagado',
  '/registro-gratuito-usuario',
  '/registro-pagado-usuario',
  '/registro-afiliado',
  '/registro-afiliado-epayco',
  '/autorizaciondatos',
  '/autorizaciondatosm',
  '/terminos-y-condiciones',
  '/cita-temporal',
  '/code-verification',
];

const isPublicPath = (pathname) =>
  PUBLIC_ROUTES.includes(pathname) || pathname.startsWith('/cita-temporal/');

// Estados iniciales
const initialState = {
  membershipStatus: null,
  availablePlans: [],
  upgradePrice: null,
  loading: false,
  error: null,
  lastUpdated: null,
  userInfo: null,
  isNewMembership: false
};

// Tipos de acciones
const MEMBERSHIP_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_MEMBERSHIP_STATUS: 'SET_MEMBERSHIP_STATUS',
  SET_AVAILABLE_PLANS: 'SET_AVAILABLE_PLANS',
  SET_UPGRADE_PRICE: 'SET_UPGRADE_PRICE',
  UPDATE_MEMBERSHIP: 'UPDATE_MEMBERSHIP',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

// Reducer
const membershipReducer = (state, action) => {
  switch (action.type) {
    case MEMBERSHIP_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: action.payload ? null : state.error
      };
    
    case MEMBERSHIP_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case MEMBERSHIP_ACTIONS.SET_MEMBERSHIP_STATUS:
      return {
        ...state,
        membershipStatus: action.payload,
        userInfo: action.payload?.userInfo || null,
        isNewMembership: action.payload?.isNewMembership || false,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString()
      };
    
    case MEMBERSHIP_ACTIONS.SET_AVAILABLE_PLANS:
      return {
        ...state,
        availablePlans: action.payload,
        loading: false,
        error: null
      };

    case MEMBERSHIP_ACTIONS.SET_UPGRADE_PRICE:
      return {
        ...state,
        upgradePrice: action.payload,
        loading: false,
        error: null
      };
    
    case MEMBERSHIP_ACTIONS.UPDATE_MEMBERSHIP:
      return {
        ...state,
        membershipStatus: action.payload,
        userInfo: action.payload?.userInfo || null,
        isNewMembership: action.payload?.isNewMembership || false,
        loading: false,
        error: null,
        lastUpdated: new Date().toISOString()
      };
    
    case MEMBERSHIP_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    default:
      return state;
  }
};

// Crear contexto
const MembershipContext = createContext();

// Hook personalizado para usar el contexto
export const useMembership = () => {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership debe ser usado dentro de un MembershipProvider');
  }
  return context;
};

// Provider del contexto
export const MembershipProvider = ({ children }) => {
  const [state, dispatch] = useReducer(membershipReducer, initialState);
  const { isAuth } = useDataUser();
  const location = useLocation();

  // Cargar estado inicial de membresía
  const loadMembershipStatus = useCallback(async () => {
    dispatch({ type: MEMBERSHIP_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const result = await membershipService.getMembershipStatus();
      
      if (result.ok) {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_MEMBERSHIP_STATUS, 
          payload: result.data 
        });
      } else {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_ERROR, 
          payload: result.error 
        });
      }
    } catch (error) {
      dispatch({ 
        type: MEMBERSHIP_ACTIONS.SET_ERROR, 
        payload: 'Error al cargar estado de membresía' 
      });
    }
  }, []);

  // Cargar planes disponibles
  const loadAvailablePlans = useCallback(async () => {
    dispatch({ type: MEMBERSHIP_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const result = await membershipService.getAvailablePlans();
      
      if (result.ok) {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_AVAILABLE_PLANS, 
          payload: result.data 
        });
      } else {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_ERROR, 
          payload: result.error 
        });
      }
    } catch (error) {
      dispatch({ 
        type: MEMBERSHIP_ACTIONS.SET_ERROR, 
        payload: 'Error al cargar planes disponibles' 
      });
    }
  }, []);

  // Cargar precio del upgrade
  const loadUpgradePrice = useCallback(async () => {
    dispatch({ type: MEMBERSHIP_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const result = await membershipService.getUpgradePrice();
      
      if (result.ok) {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_UPGRADE_PRICE, 
          payload: result.data 
        });
      } else {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_ERROR, 
          payload: result.error 
        });
      }
    } catch (error) {
      dispatch({ 
        type: MEMBERSHIP_ACTIONS.SET_ERROR, 
        payload: 'Error al cargar precio de upgrade' 
      });
    }
  }, []);

  // Actualizar membresía
  const updateMembership = async (planId) => {
    dispatch({ type: MEMBERSHIP_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const result = await membershipService.updateMembership(planId);
      
      if (result.ok) {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.UPDATE_MEMBERSHIP, 
          payload: result.data 
        });
        return { success: true };
      } else {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_ERROR, 
          payload: result.error 
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = 'Error al actualizar membresía';
      dispatch({ 
        type: MEMBERSHIP_ACTIONS.SET_ERROR, 
        payload: errorMessage 
      });
      return { success: false, error: errorMessage };
    }
  };

  // Iniciar upgrade a premium
  const initiateUpgrade = async (userId, amount = null) => {
    dispatch({ type: MEMBERSHIP_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const result = await membershipService.initiateUpgrade(userId, amount);
      
      if (result.ok) {
        return { success: true, data: result.data };
      } else {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_ERROR, 
          payload: result.error 
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = 'Error al iniciar upgrade a premium';
      dispatch({ 
        type: MEMBERSHIP_ACTIONS.SET_ERROR, 
        payload: errorMessage 
      });
      return { success: false, error: errorMessage };
    }
  };

  // Cancelar membresía premium
  const cancelPremium = async () => {
    dispatch({ type: MEMBERSHIP_ACTIONS.SET_LOADING, payload: true });
    
    try {
      const result = await membershipService.cancelPremium();
      
      if (result.ok) {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.UPDATE_MEMBERSHIP, 
          payload: result.data 
        });
        return { success: true };
      } else {
        dispatch({ 
          type: MEMBERSHIP_ACTIONS.SET_ERROR, 
          payload: result.error 
        });
        return { success: false, error: result.error };
      }
    } catch (error) {
      const errorMessage = 'Error al cancelar membresía';
      dispatch({ 
        type: MEMBERSHIP_ACTIONS.SET_ERROR, 
        payload: errorMessage 
      });
      return { success: false, error: errorMessage };
    }
  };

  // Limpiar error
  const clearError = () => {
    dispatch({ type: MEMBERSHIP_ACTIONS.CLEAR_ERROR });
  };

  // Verificar si el usuario tiene acceso a una característica
  const hasFeatureAccess = (featureName) => {
    if (!state.membershipStatus) return false;
    
    const { plan, features } = state.membershipStatus;
    
    // Si es plan premium, tiene acceso a todo
    if (plan === 'premium') return true;
    
    // Si es plan free, verificar si la característica está permitida
    return features && features.includes(featureName);
  };

  // Verificar si es plan premium
  const isPremium = () => {
    return state.membershipStatus?.plan === 'premium';
  };

  // Verificar si es plan free
  const isFree = () => {
    return state.membershipStatus?.plan === 'free';
  };

  // Verificar si puede hacer upgrade
  const canUpgrade = () => {
    return state.membershipStatus?.plan === 'free' && state.upgradePrice;
  };

  // Cargar o limpiar membresía según autenticación y ruta
  useEffect(() => {
    const tokenValidation = getAndValidateToken();
    const hasValidToken = tokenValidation.isValid;
    const onProtectedRoute = !isPublicPath(location.pathname);

    if (!hasValidToken) {
      dispatch({ type: MEMBERSHIP_ACTIONS.SET_MEMBERSHIP_STATUS, payload: null });
      return;
    }

    if (!isAuth && !onProtectedRoute) {
      return;
    }

    loadMembershipStatus();
    loadAvailablePlans();
    loadUpgradePrice();
  }, [isAuth, location.pathname, loadMembershipStatus, loadAvailablePlans, loadUpgradePrice]);

  const value = {
    // Estado
    membershipStatus: state.membershipStatus,
    availablePlans: state.availablePlans,
    upgradePrice: state.upgradePrice,
    loading: state.loading,
    error: state.error,
    lastUpdated: state.lastUpdated,
    userInfo: state.userInfo,
    isNewMembership: state.isNewMembership,
    
    // Acciones
    loadMembershipStatus,
    loadAvailablePlans,
    loadUpgradePrice,
    updateMembership,
    initiateUpgrade,
    cancelPremium,
    clearError,
    
    // Utilidades
    hasFeatureAccess,
    isPremium,
    isFree,
    canUpgrade
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
};

export default MembershipContext; 